import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter from '../common/NotificationCenter';
import config from '../../config';
import './TopNav.scss';

const TopNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getImageUrl = (imagePath?: string | null) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${imagePath}`;
  };

  const isAdmin = ['admin', 'super_admin'].includes(user?.role_name || '');

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getNavLinks = () => {
    if (!user) return [];

    switch (user.role_name) {
      case 'super_admin':
      case 'admin':
        return [
          { path: '/admin/dashboard', label: 'Dashboard' },
          { path: '/admin/relationships', label: 'Duplas' },
          { path: '/admin/milestones', label: 'Hitos' },
          { path: '/admin/scheduling', label: 'Agendamiento' },
          { path: '/admin/pdi-progress', label: 'Avance PDI' },
          { path: '/admin/ratings', label: 'Calificaciones' },
          { path: '/admin/advisory', label: 'Asesorías' },
          { path: '/admin/resources', label: 'Recursos' },
          { path: '/admin/emails', label: 'Correos' },
          { path: '/admin/analytics', label: 'Uso' },
          { path: '/mensajeria', label: 'Mensajería' },
          { path: '/admin/settings', label: 'Configuración', icon: <Settings size={17} /> }
        ];
      case 'mentor':
        return [
          { path: '/mentor/dashboard', label: 'Dashboard' },
          { path: '/mentor/mentees', label: 'Mi Mentee' },
          { path: '/mentor/plan', label: 'Acuerdo de Mentoría' },
          { path: '/mentor/sessions', label: 'Sesiones' },
          { path: '/mentor/resources', label: 'Recursos' },
          { path: '/mentor/advisory', label: 'Asesoría' },
          { path: '/mensajeria', label: 'Mensajería' }
        ];
      case 'mentee':
        return [
          { path: '/mentee/dashboard', label: 'Dashboard' },
          { path: '/mentee/plan', label: 'Mi Plan' },
          { path: '/mentee/agreement', label: 'Acuerdo de Mentoría' },
          { path: '/mentee/sessions', label: 'Sesiones' },
          { path: '/mentee/resources', label: 'Recursos' },
          { path: '/mensajeria', label: 'Mensajería' }
        ];
      default:
        return [];
    }
  };

  const getRoleLabel = () => {
    switch (user?.role_name) {
      case 'super_admin':
        return 'Super Administrador';
      case 'admin':
        return 'Administrador';
      case 'mentor':
        return 'Mentor';
      case 'mentee':
        return 'Mentee';
      default:
        return 'Usuario';
    }
  };

  if (!user) return null;

  return (
    <nav className="topnav">
      <div className="nav-brand">
        <img
          src="/img/mitsui_logo_corporate.svg"
          alt="MITSUI & CO."
          className="nav-logo"
          onClick={() => navigate('/')}
        />
        {/* El admin tiene muchas más secciones: el nombre del programa le quita
            espacio al menú, así que solo se muestra a mentores y mentees. */}
        {!isAdmin && (
          <>
            <div className="nav-divider" />
            <span className="nav-program">{config.app.programName} · {config.app.programYear}</span>
          </>
        )}
      </div>

      <div className="nav-right">
        <div className="nav-links">
          {getNavLinks().map(link => (
            <div
              key={link.path}
              className={`nav-link ${(link as any).icon ? 'nav-link--icon' : ''} ${isActive(link.path) ? 'nav-link--active' : ''}`}
              onClick={() => navigate(link.path)}
              title={link.label}
            >
              {(link as any).icon || link.label}
            </div>
          ))}
        </div>

        <div className="nav-divider nav-divider--desktop" />

        <NotificationCenter className="nav-notifications" />

        <div className="nav-user" onClick={() => setShowUserMenu(!showUserMenu)}>
          <div className="nav-avatar">
            {getImageUrl(user.profile_image) ? (
              <img
                src={getImageUrl(user.profile_image) as string}
                alt={`${user.first_name} ${user.last_name}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              getInitials(user.first_name, user.last_name)
            )}
          </div>
          <div className="nav-user-info">
            <span className="nav-username">
              {user.first_name} {user.last_name}
            </span>
            <span className="nav-role">{getRoleLabel()}</span>
          </div>

          {showUserMenu && (
            <div className="nav-dropdown">
              <div className="nav-dropdown-item" onClick={() => navigate('/profile')}>
                Mi Perfil
              </div>
              <div className="nav-dropdown-divider" />
              <div className="nav-dropdown-item nav-dropdown-item--danger" onClick={handleLogout}>
                Cerrar Sesión
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="nav-burger"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label={showMobileMenu ? 'Cerrar menú' : 'Abrir menú'}
        >
          {showMobileMenu ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {showMobileMenu && (
        <>
          <div className="nav-mobile-backdrop" onClick={() => setShowMobileMenu(false)} />
          <div className="nav-mobile-drawer">
            <div className="nav-mobile-header">
              <span className="nav-mobile-role">{getRoleLabel()}</span>
              <span className="nav-mobile-user">{user.first_name} {user.last_name}</span>
            </div>
            <div className="nav-mobile-links">
              {getNavLinks().map(link => (
                <div
                  key={link.path}
                  className={`nav-mobile-link ${isActive(link.path) ? 'nav-mobile-link--active' : ''}`}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </div>
              ))}
            </div>
            <div className="nav-mobile-footer">
              <div className="nav-mobile-link" onClick={() => navigate('/profile')}>Mi Perfil</div>
              <div className="nav-mobile-link nav-mobile-link--danger" onClick={handleLogout}>Cerrar Sesión</div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default TopNav;